import find from 'lodash/find';
import BaseSubscription from './BaseSubscription';
import { browserHistory } from '../browserHistory';
import { store } from '../store';

import {
  getCompanyList,
  getCurrentOperator,
  getCompanyOperators,
  getAuthTokenByEmailAndPassword,
} from './apiGlobal';

import {
  setIsLoggedIn,
  setAuthToken,
  setCompanies,
  setCurrentCompany,
  setCurrentOperator,
  setCurrentCompanyOperators,
} from '../actions/actions';

import { getFromLocalStorage, parseGETParams, setToLocalStorage } from '../utils/utilsGlobal';
import { generateRoomURL, isChatURL, parseRoomURL } from '../utils/utilsChat';
import GraphQLClient, { gql } from './GraphQLClient';

export const USER_LOGGED_IN = 'USER_LOGGED_IN';
export const USER_LOGGED_OUT = 'USER_LOGGED_OUT';
export const USER_SET_SUCCESS = 'USER_SET_SUCCESS';
export const USER_SET_ERROR = 'USER_SET_ERROR';


export default class UserWatcher extends BaseSubscription {

  companies = [];
  currentOperator = {};
  currentCompany = {};
  currentCompanyOperators = [];

  initialize = () => {
    this.checkIfLoggedIn().then(isLoggedIn => {
      if (isLoggedIn) {
        store.dispatch(setIsLoggedIn(true));

        const authToken = store.getState().authToken;
        this.triggerEvent(USER_LOGGED_IN, authToken, { firedOnce: true });
        this.onLoggedIn();
      }
      else {
        const message = 'UserWatcher: Not logged in';
        this.triggerEvent(USER_SET_ERROR, { error: message }, { firedOnce: true });
        browserHistory.push(this.generateLoginRedirectPath());
      }
    });
  };

  getCompanyById = (companyId) => {
    return find(this.companies, { id: companyId });
  };

  checkIfLoggedIn = () => {
    const authToken = store.getState().authToken;
    if (!authToken) {
      return Promise.resolve(false);
    }
    const query = gql`
      query {
        companies { id }
      }
    `;
    const graphQLClient = new GraphQLClient();
    graphQLClient.initialize({
      url: process.env.ADMIN_API_URL,
      token: authToken,
    });
    return graphQLClient.query(query, {})
      .then(({ companies }) => {
        if (companies?.length) {
          return true;
        }
        else {
          throw false;
        }
      })
      .catch(() => false);
  };

  onLoggedIn = () => {
    return this.fetchAndSetCompanies().then(() => {
      const initialCompany = this.setInitialCompany();

      return this.fetchAndSetCurrentOperator(initialCompany.id)
        .then(() => this.fetchAndSetCurrentCompanyOperators(initialCompany.id))
        .then(() => {
          this.triggerEvent(USER_SET_SUCCESS, initialCompany, { firedOnce: true });
        });
    });
  };

  fetchAndSetCompanies = () => {
    return getCompanyList()
      .then(companies => {
        if (companies?.length) {
          this.companies = companies;
          store.dispatch(setCompanies(companies));
        }
        else {
          const message = 'UserWatcher: Companies not found';
          this.triggerEvent(USER_SET_ERROR, { error: message }, { firedOnce: true });
        }
      })
      .catch(error => {
        error.overrideErrorNotification({ alert: false });
        this.triggerEvent(USER_SET_ERROR, { error }, { firedOnce: true });
        throw error;
      });
  };

  fetchAndSetCurrentOperator = (companyId) => {
    return getCurrentOperator(companyId)
      .then(currentOperator => {
        if (currentOperator.id) {
          return currentOperator;
        }
        else {
          throw currentOperator;
        }
      })
      .then(currentOperator => {
        this.currentOperator = currentOperator;
        store.dispatch(setCurrentOperator(currentOperator));
        return currentOperator;
      })
      .catch(error => {
        this.triggerEvent(USER_SET_ERROR, { error }, { firedOnce: true });
      });
  };

  fetchAndSetCurrentCompanyOperators = (companyId) => {
    return getCompanyOperators(companyId)
      .catch(() => {
        return [this.currentOperator];
      })
      .then(currentCompanyOperators => {
        return currentCompanyOperators?.length ? currentCompanyOperators : [this.currentOperator];
      })
      .then(currentCompanyOperators => {
        this.currentCompanyOperators = currentCompanyOperators;
        store.dispatch(setCurrentCompanyOperators(currentCompanyOperators));
        return currentCompanyOperators;
      });
  };

  setInitialCompany = () => {
    let { companyId } = parseRoomURL(window.location.pathname);
    let initialCompany = this.getCompanyById(companyId);

    if (!initialCompany) {
      initialCompany = this.getCompanyById( getFromLocalStorage('CURRENT_COMPANY_ID') );
    }
    if (!initialCompany) {
      initialCompany = this.companies[0];
    }
    this.currentCompany = initialCompany;
    setToLocalStorage('CURRENT_COMPANY_ID', initialCompany.id);
    store.dispatch(setCurrentCompany(initialCompany));

    if (isChatURL(window.location.pathname)) {
      this.redirectToCompany(initialCompany.id);
    }
    return initialCompany;
  };

  redirectToCompany = (companyId) => {
    const { roomId } = parseRoomURL(window.location.pathname);
    const currentCompanyPath = generateRoomURL(companyId, roomId);
    browserHistory.push(currentCompanyPath);
  };

  logIn = (email, password) => {
    return getAuthTokenByEmailAndPassword(email, password)
      .then(authToken => {
        if (authToken) {
          localStorage.setItem('TOKEN', authToken); // TODO: fix client-side auth security
          store.dispatch(setAuthToken(authToken));
          store.dispatch(setIsLoggedIn(true));
          this.triggerEvent(USER_LOGGED_IN, authToken, { firedOnce: true });
          this.onLoggedIn();
          browserHistory.push( this.extractRedirectPathAfterLogin() );
          return authToken;
        }
        else {
          throw authToken;
        }
      });
  };

  logOut = () => {
    this.triggerEvent(USER_LOGGED_OUT, null, { firedOnce: true });
    setTimeout(() => {
      this.resetUserSpecificLocalSettings();
      store.dispatch(setIsLoggedIn(false));
      location.pathname = '/login';
    });
  };

  generateLoginRedirectPath = () => {
    const { pathname, search } = window.location;
    if (pathname.startsWith('/login')) {
      return pathname + search;
    }
    if (pathname === '/') {
      return '/login';
    }
    return '/login?from=' + encodeURIComponent(pathname);
  };

  extractRedirectPathAfterLogin = () => {
    const params = parseGETParams(window.location.search);
    if (!params.from) {
      return '/';
    }
    const link = document.createElement('a');
    link.href = params.from;
    return link.pathname; // make sure to remove host from redirect URL in order to prevent open redirect vulnerability
  };

  resetCompanySpecificLocalSettings = () => {
    localStorage.removeItem('ROOM_FILTER');
    localStorage.removeItem('CURRENT_COMPANY_ID');
  };

  resetUserSpecificLocalSettings = () => {
    this.resetCompanySpecificLocalSettings();
    localStorage.removeItem('TOKEN'); // TODO: fix client-side auth security
    localStorage.removeItem('OPENED_TABS');
    localStorage.removeItem('IS_MUTED');
    localStorage.removeItem('ISSUES_FILTERS');
    localStorage.removeItem('LATEST_UNSCHEDULED_BREAK_STARTED_AT');
    this.resetLocalSettingByKeyPrefix('TYPED_TEXT_');
    this.resetLocalSettingByKeyPrefix('TYPED_MASS_MESSAGE_');
    this.resetLocalSettingByKeyPrefix('LATEST_OPENED_ROOM_');
  };

  resetLocalSettingByKeyPrefix = (keyPrefix) => {
    Object.keys(localStorage)
      .filter(key => key.startsWith(keyPrefix))
      .forEach(key => localStorage.removeItem(key));
  };
}
